import React from "react";
import { LuMapPin, LuBuilding, LuGlobe, LuPhone, LuMail } from "react-icons/lu";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

// components
import TextInput from "../../../components/elements/form/TextInput";

// dispatch
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  ICreateCompany,
  ICreateCompanyError,
} from "../../../store/@types/company";
import SelectInput from "../../../components/elements/form/SelectInput";
import {
  clearStates,
  disableLocationsLoading,
} from "../../../store/slices/locations";
import { get_countries, get_states } from "../../../service/api/locations";
import Button from "../../../components/elements/form/Button";
import { create_company } from "../../../service/api/company";
import { useNavigate } from "react-router-dom";

type IAddCompany = {
  rc_number: string;
};

const AddCompany: React.FC<IAddCompany> = ({ rc_number }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(({ company }) => company.loading);
  const countries = useAppSelector(({ locations }) => locations.countries);
  const states = useAppSelector(({ locations }) => locations.states);
  const locationsLoading = useAppSelector(({ locations }) => locations.loading);

  React.useEffect(() => {
    if (countries.length === 0) {
      void get_countries(dispatch);
    }
  }, [countries.length, dispatch]);

  const [data, dataSet] = React.useState<ICreateCompany>({
    rc_number: rc_number,
    company_name: "",
    company_phone: "",
    company_email: "",
    company_website: "",
    company_address: "",
    company_country: "",
    company_state: "",
  });
  const [error, errorSet] = React.useState<ICreateCompanyError>({
    rc_number: "",
    company_name: "",
    company_phone: "",
    company_email: "",
    company_website: "",
    company_address: "",
    company_country: "",
    company_state: "",
  });

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dataSet((prev) => ({ ...prev, [name]: value }));
    errorSet((prev) => ({ ...prev, [name]: "" }));
  };

  return (
    <div className="bg-white rounded-xl p-6 mt-4 shadow-md space-y-6">
      <SelectInput
        label="Select Country"
        items={countries.map(({ id, name }) => ({
          id: String(id),
          name,
        }))}
        name="company_country"
        value={data.company_country}
        onChange={(e) => {
          const { name, value } = e.target;
          dispatch(clearStates());
          if (value) {
            void get_states(dispatch, value);
          } else {
            dispatch(disableLocationsLoading());
          }
          dataSet((prev) => ({
            ...prev,
            [name]: value,
            company_state: "",
          }));
          errorSet((prev) => ({ ...prev, [name]: "" }));
          errorSet((prev) => ({
            ...prev,
            [name]: "",
            company_state: "",
          }));
        }}
        status={error.company_country}
        className="capitalize"
        loading={locationsLoading && countries.length === 0}
      />
      <SelectInput
        label="Select State"
        items={states.map(({ id, name }) => ({
          id: String(id),
          name,
        }))}
        name="company_state"
        value={data.company_state}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          const { name, value } = e.target;
          dataSet((prev) => ({ ...prev, [name]: value }));
          errorSet((prev) => ({ ...prev, [name]: "" }));
        }}
        status={error?.company_state}
        loading={locationsLoading && Boolean(data.company_country)}
      />

      <TextInput
        type="text"
        icon={<LuBuilding size={16} className="text-primary-500" />}
        label="Company Name"
        name="company_name"
        value={data.company_name}
        onChange={onChange}
        placeholder="Corporate Setup"
        required={true}
        status={error.company_name}
      />
      <TextInput
        type="text"
        icon={<LuMail size={16} className="text-primary-500" />}
        label="Company Email"
        name="company_email"
        value={data.company_email}
        onChange={onChange}
        placeholder="user@email.com"
        required={true}
        status={error.company_email}
      />
      <TextInput
        type="text"
        icon={<LuGlobe size={16} className="text-primary-500" />}
        label="Company Website"
        name="company_website"
        value={data.company_website}
        onChange={onChange}
        placeholder="https://corporate-setup.app"
        required={true}
        status={error.company_website}
      />
      <div className="w-full">
        <label className="flex items-center gap-3 text-md font-medium">
          Company Phone
        </label>
        <div
          className={`flex w-full h-11 border items-center rounded-lg mt-1 text-gray-900 px-3 py-2 focus-within:ring focus-within:ring-primary-100/30 ${
            error.company_phone
              ? "border-[red]"
              : "border-[#e5e7eb] focus-within:border-primary-500"
          }`}
        >
          <div className="mr-2 border-r border-r-gray-50/20 pr-2 text-black">
            <LuPhone size={16} className="text-primary-500" />
          </div>
          <PhoneInput
            defaultCountry="NG"
            international
            countryCallingCodeEditable={false}
            value={data.company_phone || undefined}
            onChange={(value) => {
              dataSet((prev) => ({ ...prev, company_phone: value ?? "" }));
              errorSet((prev) => ({ ...prev, company_phone: "" }));
            }}
            numberInputProps={{
              name: "company_phone",
              id: "company_phone",
              required: true,
              placeholder: "+234 703 304 6371",
              className:
                "PhoneInputInput w-full !outline-none px-0 py-0 text-sm border-none focus:border-transparent focus:ring-0",
            }}
            className="PhoneInput flex-1 gap-2"
          />
        </div>
      </div>
      <TextInput
        type="text"
        icon={<LuMapPin size={16} className="text-primary-500" />}
        label="Company Address"
        name="company_address"
        value={data.company_address}
        onChange={onChange}
        placeholder="Ogba-Ikeja"
        required={true}
        status={error.company_address}
      />

      <Button
        title="Register"
        loading={loading}
        onHandler={() => {
          let validate: boolean = false;

          if (!data.rc_number) {
            validate = true;
            errorSet((prev) => ({ ...prev, rc_number: "warning" }));
          }
          if (!data.company_name) {
            validate = true;
            errorSet((prev) => ({ ...prev, company_name: "warning" }));
          }
          if (!data.company_phone) {
            validate = true;
            errorSet((prev) => ({ ...prev, company_phone: "warning" }));
          }
          if (!data.company_email) {
            validate = true;
            errorSet((prev) => ({ ...prev, company_email: "warning" }));
          }
          if (!data.company_website) {
            validate = true;
            errorSet((prev) => ({ ...prev, company_website: "warning" }));
          }
          if (!data.company_address) {
            validate = true;
            errorSet((prev) => ({ ...prev, company_address: "warning" }));
          }
          if (!data.company_country) {
            validate = true;
            errorSet((prev) => ({ ...prev, company_country: "warning" }));
          }
          if (!data.company_state) {
            validate = true;
            errorSet((prev) => ({ ...prev, company_state: "warning" }));
          }

          if (validate) return;
          create_company(dispatch, data, navigate);
        }}
        className="bg-primary-500 hover:bg-primary-900 text-white rounded-lg"
      />
    </div>
  );
};

export default AddCompany;
